import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';
import TeamCard from '../components/TeamCard';
import CTASection from '../components/CTASection';
import { team } from '../data/team';
import { useSEO } from '../hooks/useSEO';
import { PAGE_SEO } from '../utils/seo';

const values = [
  {
    title: 'Quality Over Quantity',
    description: 'We would rather deliver one exceptional product than ten mediocre ones. Every project receives our full attention — every animation is intentional, every line of code is purposeful, every pixel is placed with care. We don\'t pad timelines or inflate scope. We focus on what matters and execute it brilliantly.',
  },
  {
    title: 'Privacy by Design',
    description: 'In an era where every app tracks, sells, and exploits user data, we take a different path. Our apps Wallet and Path collect zero user data. No analytics, no crash reporting to third parties, no accounts, no cloud sync unless explicitly requested. We believe privacy isn\'t a feature — it\'s a fundamental right. We design architectures that deliver full functionality while collecting nothing, because the best data is data you never collect.',
  },
  {
    title: 'User-Centered Craft',
    description: 'We design and build with your users in mind — not your competitors, not trends, not vanity metrics. Intuitive interfaces, fast loading, and accessibility are not afterthoughts — they are the foundation. We study how real people interact with products, what frustrates them, what delights them, and we let those insights drive every design and development decision.',
  },
  {
    title: 'Radical Transparency',
    description: 'Clear communication, honest timelines, no surprises. You will always know where your project stands, what decisions are being made, and what comes next. We share work in progress, invite feedback at every stage, and never hide behind jargon. If something takes longer, you\'ll know why. If something isn\'t working, we\'ll tell you — and fix it.',
  },
  {
    title: 'Build to Last',
    description: 'We build for maintainability, scalability, and the future. Clean architecture, modern frameworks, thorough documentation, and thoughtful code organization keep your product healthy for years. We don\'t write code that works for now — we write code that your team can understand, extend, and rely on long after we\'ve delivered.',
  },
  {
    title: 'Shipping is the Standard',
    description: 'Ideas are cheap. Execution is everything. We don\'t deliver mockups and call it done. We ship real, working products — live on the Play Store, deployed on production servers, installed on real devices. Our portfolio isn\'t a collection of concepts; it\'s a collection of published, user-facing products. That\'s the standard we hold ourselves to, and the standard we deliver to you.',
  },
];

const approachPillars = [
  {
    number: '01',
    title: 'Deep Understanding',
    description: 'Before any design or code, we immerse ourselves in the problem. Who are the users? What are their frustrations? What does success look like — not in abstract metrics, but in real human outcomes? We research your industry, study your competitors, and map the landscape so we know exactly where to play and how to win.',
    detail: 'This phase often reveals opportunities you hadn\'t considered and pitfalls you hadn\'t anticipated. We don\'t rush it.',
  },
  {
    number: '02',
    title: 'Strategic Design',
    description: 'We translate understanding into design. This means wireframes that solve real user problems, visual designs that communicate your brand\'s personality, and interaction patterns that feel natural. We iterate rapidly — exploring multiple directions, gathering feedback, and converging on the strongest solution. Every design decision is justified by user need, not aesthetic preference alone.',
    detail: 'We present options, not ultimatums. You\'ll see multiple creative routes and understand the trade-offs of each.',
  },
  {
    number: '03',
    title: 'Rigorous Development',
    description: 'This is where craft meets engineering. We build with clean architecture, modern frameworks, and disciplined code practices. Every feature is developed, tested, and optimized. We handle edge cases, error states, and performance bottlenecks before they reach users. Code reviews, linting, and automated checks ensure consistency. The result is software that\'s fast, stable, and a pleasure to maintain.',
    detail: 'We don\'t cut corners on testing, accessibility, or performance. These aren\'t optional — they\'re part of the job.',
  },
  {
    number: '04',
    title: 'Polished Delivery',
    description: 'We don\'t hand over unfinished work. Every project ships with production-ready assets, comprehensive documentation, and everything your team needs to carry it forward. For apps, that means store listings optimized for conversion. For websites, that means deployment with monitoring. For designs, that means brand guidelines that your team can actually use. We stay until the handoff is complete.',
    detail: 'Delivery isn\'t the end — it\'s the beginning of a long-term relationship. We\'re here for maintenance, iteration, and growth.',
  },
];

export default function AboutPage() {
  // Apply SEO
  useSEO(PAGE_SEO.about);
  return (
    <PageTransition>
      {/* Hero */}
      <section className="section">
        <div className="container">
          <ScrollReveal>
            <h1 className="h-1" style={{ marginBottom: 'var(--space-4)' }}>
              About Us
            </h1>
            <p className="body-lg" style={{ color: 'var(--text-secondary)', maxWidth: '720px', lineHeight: 1.7 }}>
              Two people. One standard. We build digital products that are private by design, performant by default, and crafted to a level of quality that speaks for itself.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Our Story */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <h2 className="h-2" style={{ marginBottom: 'var(--space-6)', color: 'var(--accent-green)' }}>Our Story</h2>
            <div style={{ maxWidth: '760px' }}>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.8, marginBottom: 'var(--space-5)' }}>
                We didn't start as a studio. We started as developers and designers who were frustrated by the state of software — bloated apps that tracked everything, websites that looked the same, brands that felt hollow. We believed software could be better: faster, more private, more beautiful, more honest.
              </p>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.8, marginBottom: 'var(--space-5)' }}>
                So we built things ourselves. Sidhant designed and published Wallet — a financial app that stores everything on-device, collects zero data, and has earned strong user ratings for its privacy-first approach. He built Path, a step counter that respects your health data as much as you do. He created Nuzzle, an image puzzle game that turns Pexels photography into engaging gameplay. And Calculator — a utility app so clean and fast that users praise it in a category known for being utterly forgettable.
              </p>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.8, marginBottom: 'var(--space-5)' }}>
                Akanksha brought these products to life visually. She designed complete subreddit identities for r/90sHipHop, r/CleverComeback, and r/youtubeindia — each with custom logos, desktop banners, and mobile-optimized banners. She built collections of 13+ logos and 14+ banners spanning every genre from music to gaming to community culture. She designed websites for brands in beauty, luxury retail, conservation, and SaaS — each with a distinct visual personality and flawless responsive execution.
              </p>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.8, marginBottom: 'var(--space-5)' }}>
                Together, we became a studio — not because we wanted to scale, but because we wanted to bring our combined excellence to clients who share our standards. Every project we take on receives the same obsessive attention we give our own products. We don't have a large team because we don't need one. We have the right team.
              </p>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.8 }}>
                Our story isn't about growth metrics or funding rounds. It's about shipping real products that real people use, and building a reputation on the quality of work that stands on its own. That's the story we're still writing — one project at a time.
              </p>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Our Approach */}
      <ScrollReveal>
        <section className="section" style={{ borderTop: '1px solid var(--separator)' }}>
          <div className="container">
            <h2 className="h-2" style={{ marginBottom: 'var(--space-3)', color: 'var(--text-announcement)' }}>Our Approach</h2>
            <p className="body" style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-10)', maxWidth: '600px', lineHeight: 1.7 }}>
              Every project follows a disciplined four-pillar process — structured enough to deliver consistently, flexible enough to stay creative.
            </p>

            {approachPillars.map((pillar, i) => (
              <div
                key={pillar.number}
                className="card"
                style={{
                  padding: 'var(--space-8)',
                  marginBottom: i < approachPillars.length - 1 ? 'var(--space-6)' : 0,
                  display: 'flex',
                  gap: 'var(--space-6)',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-title)',
                    fontWeight: 800,
                    fontSize: '2rem',
                    lineHeight: 1,
                    color: 'var(--accent-green)',
                    flexShrink: 0,
                    minWidth: '56px',
                  }}
                >
                  {pillar.number}
                </span>
                <div>
                  <h3 className="h-3" style={{ marginBottom: 'var(--space-3)' }}>{pillar.title}</h3>
                  <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-3)' }}>
                    {pillar.description}
                  </p>
                  <p className="caption" style={{ color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.6 }}>
                    {pillar.detail}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </ScrollReveal>

      {/* Our Values */}
      <ScrollReveal>
        <section className="section" style={{ borderTop: '1px solid var(--separator)' }}>
          <div className="container">
            <h2 className="h-2" style={{ marginBottom: 'var(--space-3)', color: 'var(--text-warning)' }}>Our Values</h2>
            <p className="body" style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-10)', maxWidth: '600px', lineHeight: 1.7 }}>
              These aren't posters on a wall. These are the principles that guide every decision we make, from which projects we accept to how we write every line of code.
            </p>
            <div className="grid-2">
              {values.map((value, i) => (
                <ScrollReveal key={value.title} delay={i * 80}>
                  <div className="card" style={{ padding: 'var(--space-6)', height: '100%' }}>
                    <h3 className="h-4" style={{ marginBottom: 'var(--space-3)', color: 'var(--accent-green)' }}>
                      {value.title}
                    </h3>
                    <p className="caption" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                      {value.description}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Team */}
      <ScrollReveal>
        <section className="section" style={{ borderTop: '1px solid var(--separator)' }}>
          <div className="container">
            <h2 className="h-2" style={{ marginBottom: 'var(--space-3)' }}>The Team</h2>
            <p className="body" style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-10)', maxWidth: '560px', lineHeight: 1.7 }}>
              Small by design. We don't scale headcount — we scale excellence. Two people, covering every discipline needed to ship complete products.
            </p>
            <div className="grid-2" style={{ maxWidth: '800px' }}>
              {team.map((member) => (
                <TeamCard key={member.id} member={member} />
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal>
        <section className="section" style={{ borderTop: '1px solid var(--separator)' }}>
          <div className="container">
            <CTASection
              title="Want to build something great?"
              subtitle="Whether it's your first app or your next platform, we'd love to help."
              primaryLabel="Get in Touch"
              secondaryLabel="View Our Work"
              primaryHref="/contact"
              secondaryHref="/work"
            />
          </div>
        </section>
      </ScrollReveal>
    </PageTransition>
  );
}
